import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { StatsService } from "../../../services/stats/stats.service";
import { BaseComponent } from "../../base/base.component";
import { PaymentService } from "../../../services/payment/payment.service";
import { SeasonService } from "../../../services/season/season.service";
import * as moment from 'moment';
import {
    ChartComponent,
    ApexAxisChartSeries,
    ApexChart,
    ApexXAxis,
    ApexDataLabels,
    ApexTitleSubtitle,
    ApexStroke,
    ApexGrid,
    ApexYAxis,
    ApexTooltip,
    ApexPlotOptions
} from "ng-apexcharts";
import 'moment/locale/it';

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    dataLabels: ApexDataLabels;
    grid: ApexGrid;
    stroke: ApexStroke;
    title: ApexTitleSubtitle;
    tooltip: ApexTooltip;
    plotOptions: ApexPlotOptions;
};

@Component({
    selector: 'app-recap',
    templateUrl: './recap.component.html',
    styleUrls: ['./recap.component.scss']
})
export class RecapComponent extends BaseComponent implements OnInit {

    @ViewChild("chart") chart: ChartComponent | undefined;
    public chartOptions: Partial<ChartOptions> | any;

    @Input('title') public title: string = 'Riepilogo attività';
    @Input('card') public cards: any;
    @Input('adminPage') public adminPage: boolean = false;
    @Input('agentPage') public agentPage: boolean = false;
    @Input('showHeader') public showHeader: boolean = true;

    loading = true;

    selectedPeriod: string = 'season';
    companyId: any = localStorage.getItem('wisper_companyId');

    constructor(
        public statService: StatsService,
        private paymentService: PaymentService,
        private seasonService: SeasonService,
        private router: Router,
        public serviceInjector: Injector
    ) {
        super(serviceInjector);

        this.cards = [
            'Iscritti stagione',
            'Quote incassate',
            'Quote da incassare',
            'Totale incassato'
        ];

        this.chartOptions = {
            series: [
                {
                    name: "Pagamenti",
                    data: []
                }
            ],
            chart: {
                height: 350,
                type: "bar",
                toolbar: {
                    show: false
                },
                zoom: {
                    enabled: false
                }
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    columnWidth: '50%',
                }
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                show: true,
                width: 2,
                colors: ["transparent"]
            },
            xaxis: {
                categories: []
            },
            yaxis: {
                title: {
                    text: "Importo (€)"
                },
                labels: {
                    formatter: function (val: number) {
                        return val.toFixed(2).replace('.', ',');
                    }
                }
            },
            fill: {opacity: 1, colors: ['#00ae9e']},
            tooltip: {
                y: {
                    formatter: function (val: number) {
                        return "€ " + val.toFixed(2);
                    }
                }
            }
        };
    }

    async ngOnInit() {
        await this.loadStats();
        await this.loadChartData();
    }

    async loadStats() {
        let success;
        let data;

        if (this.adminPage) {
            let response = await this.statService.getAdminStatsTotal();
            success = response.success;
            data = response.data;
        } else if (this.agentPage) {
            let response = await this.statService.getAgentStatsTotal(this.getCurrentUser());
            success = response.success;
            data = response.data;
        } else {
            let response = await this.statService.getCompanyStats(this.companyId);
            success = response.success;
            data = response.data;
        }

        if (success) {
            if (this.adminPage) {
                this.cards = [
                    { title: 'Totale società', quantity: data.totalCompanies, icon: 'bi-building' },
                    { title: 'Quote incassate', quantity: data.totalQuotes, icon: 'bi-check-circle-fill' },
                    { title: 'Incasso abbonamenti', quantity: `${new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(data.totalRevenues || 0)}`, icon: 'bi-cash-stack' },
                ];
            } else if (this.agentPage) {
                this.cards = [
                    { title: 'Totale società attive', quantity: data.totalActiveCompanies, icon: 'bi-building-check' },
                    { title: 'Totale società da approvare', quantity: data.totalPendingCompanies, icon: 'bi-building-exclamation' },
                    { title: 'Totale atleti nelle società', quantity: data.totalCompaniesAthletes, icon: 'bi-people-fill' },
                ];
            } else {
                this.cards = [
                    {
                        title: 'Iscritti stagione',
                        quantity: data.totalAthletes,
                        icon: 'bi-people-fill',
                        subtitle: data.totalCompanyAthletes != null ? `su ${data.totalCompanyAthletes} totali` : undefined,
                    },
                    { title: 'Quote incassate', quantity: data.totalQuotesCashed, icon: 'bi-check-circle-fill' },
                    { title: 'Quote da incassare', quantity: data.totalQuotesToCashIn, icon: 'bi-clock-history' },
                    { title: 'Totale incassato', quantity: `${new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(data.totalquotesSum || 0)}`, icon: 'bi-wallet2' },
                ];
            }
        } else {
            const icons: any = {
                'Iscritti stagione': 'bi-people-fill',
                'Quote incassate': 'bi-check-circle-fill',
                'Quote da incassare': 'bi-clock-history',
                'Totale incassato': 'bi-wallet2',
                'Totale società': 'bi-building',
                'Incasso abbonamenti': 'bi-cash-stack',
                'Totale società attive': 'bi-building-check',
                'Totale società da approvare': 'bi-building-exclamation',
                'Totale atleti nelle società': 'bi-people-fill'
            };
            this.cards = this.cards.map((card: any) => {
                return {
                    title: card, quantity: 0, icon: icons[card] || 'bi-info-circle'
                }
            })
        }

        this.loading = false;
    }

    async setPeriod(period: string) {
        this.selectedPeriod = period;
        await this.loadChartData();
    }

    async loadChartData() {
        if (this.agentPage) return;
        let params: any = {
            status: 'paid',
            revenueView: true
        };
        if (this.companyId && !this.adminPage) {
            params.companyId = this.companyId;
        }
        const response = await this.paymentService.getAllPayments(params);
        if (response && response.success) {
            const payments = response.data;

            const seasonsResponse = await this.seasonService.getSeasons(this.companyId);

            let currentSeason = null;

            if (seasonsResponse && seasonsResponse.success) {
                currentSeason = seasonsResponse.data.find(
                    (season: any) => season.isCurrent === 1
                );
            }

            this.processChartData(payments, currentSeason);
        }
    }

    processChartData(payments: any[], currentSeason?: any) {
        let groupedData: { [key: string]: number } = {};
        let categories: string[] = [];
        let seriesData: number[] = [];
        const now = moment();
        moment.locale('it');
        if (this.selectedPeriod === 'week') {
            const startOfWeek = now.clone().startOf('week');

            for (let i = 0; i < 7; i++) {
                const day = startOfWeek.clone().add(i, 'days');
                groupedData[day.format('ddd')] = 0;
            }

            payments.forEach(p => {
                const date = moment(p.createdAt, "DD/MM/YYYY HH:mm");
                const amount = parseFloat(p.amount) || 0;

                if (date.isSame(now, 'week')) {
                    const key = date.format('ddd');
                    if (groupedData[key] !== undefined) {
                        groupedData[key] += amount;
                    }
                }
            });

            categories = Object.keys(groupedData);
            seriesData = Object.values(groupedData);
        } else if (this.selectedPeriod === 'month') {
            const daysInMonth = now.daysInMonth();

            for (let i = 1; i <= daysInMonth; i++) {
                groupedData[i.toString()] = 0;
            }

            payments.forEach(p => {
                const date = moment(p.createdAt, "DD/MM/YYYY HH:mm");
                const amount = parseFloat(p.amount) || 0;

                if (date.isSame(now, 'month')) {
                    const key = date.date().toString();
                    groupedData[key] += amount;
                }
            });

            categories = Object.keys(groupedData);
            seriesData = Object.values(groupedData);
        } else if (this.selectedPeriod === 'season') {

            // Se non è disponibile la stagione corrente, non elaboriamo il grafico
            if (!currentSeason) {
                this.chartOptions.series = [{
                    name: "Pagamenti",
                    data: []
                }];
                this.chartOptions.xaxis = {
                    categories: []
                };
                return;
            }
            const startSeason = moment(currentSeason.startDate).startOf('month');
            const endSeason = moment(currentSeason.endDate).startOf('month');

            let current = startSeason.clone();

            // Crea una voce per ogni mese della stagione corrente
            while (current.isSameOrBefore(endSeason, 'month')) {
                const key = current
                    .locale('it')
                    .format('MMM YYYY')
                    .replace('.', '')
                    .replace(/^\w/, c => c.toUpperCase());

                groupedData[key] = 0;
                current.add(1, 'month');
            }
            payments.forEach(p => {
                const date = moment(p.createdAt, "DD/MM/YYYY HH:mm");
                const amount = parseFloat(p.amount) || 0;

                if (date.isSameOrAfter(startSeason) && date.isSameOrBefore(endSeason.clone().endOf('month'))) {
                    const key = date.locale('it').format('MMM YYYY').replace('.', '').replace(/^\w/, c => c.toUpperCase());

                    if (groupedData[key] !== undefined) {
                        groupedData[key] += amount;
                    }
                }
            });

            categories = Object.keys(groupedData);
            seriesData = Object.values(groupedData);
        }

        this.chartOptions.series = [{
            name: "Pagamenti",
            data: seriesData
        }];
        this.chartOptions.xaxis = {
            categories: categories
        };
        if (this.chart) {
            this.chart.updateSeries(this.chartOptions.series, true);
        }
    }

    onCardClick(card: any) {
        if (card.title === 'Quote da incassare') {
            this.utils.activeMenuSubject.next(6);
            this.router.navigate(['/companies/athletes'], { queryParams: { missingPayment: true } });
        }
    }
}
